import { fg } from '@atlaskit/platform-feature-flags';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { getCreatedBy } from '@atlassian/search-client/created-by';
import { type User, usersQuery } from '@atlassian/search-client/user/user';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys3P } from '../../../../../common/constants/products';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getUrsProductKey } from '../utils';
import { loadRecommendedUsers } from '../utils/recommendedUser';

const MAX_SEARCHED_OPTIONS_RESULTS = 5;

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('createdBy');

const loadInitialOptions = async (config: CloudConfig) => {
	return loadRecommendedUsers(config);
};

const searchCreatedBy = async (query: string, config: CloudConfig) => {
	if (!config.intl) {
		return []; // base on existing interface, can only query with intl
	}

	const response = await getCreatedBy({
		query,
		cloudId: config.cloudId || '',
		userId: config.userId || '',
		productKey: fg('search_page_filter_product_key')
			? getUrsProductKey(config.primaryProduct)
			: ProductKeys.Confluence,
		maxNumberOfResults: MAX_SEARCHED_OPTIONS_RESULTS,
		intl: config.intl,
	});

	if (!response) {
		return [];
	}

	return response.map((user) => ({
		trackingKey: user.id,
		value: user.id,
		label: user.name,
		avatarUrl: user.avatarUrl,
		queryParamValue: user.id,
		product: config.product,
	}));
};

export const filter: Readonly<{
	id: 'createdBy';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'createdBy'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'createdBy'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'createdBy'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'createdBy',
	products: [...products3PFromConfig],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'createdBy',
		fetcher: async (queryParams, config) => {
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});
			const initialUsersResponse = await usersQuery(queryParams, headers);

			const allCreatedBy = initialUsersResponse.data?.users ?? ([] satisfies User[]);

			const userIds = new Set();

			const createdByOptions = allCreatedBy
				.filter((user) => !userIds.has(user.accountId) && userIds.add(user.accountId)) // Remove duplicates
				.map((user) => ({
					trackingKey: user.accountId,
					value: user.accountId,
					label: user.name,
					avatarUrl: user.picture,
					queryParamValue: user.accountId,
					product: config.product,
				}));
			return createdByOptions;
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return loadInitialOptions(config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return searchCreatedBy(query, config);
		},
	},
});
